<template>
  <div>
    <h1>{{ $t('views.room.title') }}</h1>
    <loading-container :state="state" errorLinkTo="/" :i18nErrorMessage="getI18nErrorString()">

      <link-shopping-cart />

      <div v-if="!showMaxReservations && !showDuplicateReservation">
        <h2 class="mt-3 mb-1 room-h2">{{ room.name }}</h2>
        <p class="mb-2">{{ room.description }}</p>
      </div>

      <error-message v-if="showMaxReservations" :i18nMessage="$t('common.maxReservations', maxReservations)" class="mt-2" />

      <error-message v-if="showDuplicateReservation" :i18nMessage="$t('common.duplicateReservation')" class="mt-2">
        <div class="row mx-auto">
          <div class="col-12 mt-2 px-0">
            <button-cancel v-on:click="showDuplicateReservation = false" :i18nMessage="$t('common.backToOverview')" class="btn-to-danger-link text-black mt-3" />
          </div>
        </div>
      </error-message>

      <div v-if="!showMaxReservations && !showDuplicateReservation">
        <link-arrow i18nMessage="views.room.chooseAnotherRoom" to="/" class="mb-1" />
        <calendar-month-selector :calendarForDay="calendarForDay" v-on:changed="changeMonth($event)" class="mt-3" />
        <calendar-monthly :room="room" :days="days" v-on:clicked="onTimeSlotClicked($event)" class="calendar-mobile" />
        <p class="mt-3">
          {{ $tc('views.room.infoTextCount', { count: remainingReservationsCount(), max: maxReservations} ) }}
          {{ hasRemainingTime() && $tc('views.room.infoTextUnblock', remainingMinutes()) || '' }}
          </p>
      </div>

      <div class="row my-3">
          <div class="col-12 col-md-4 offset-md-4">
            <link-default i18nMessage="common.goToYourReservationsSimple" to="/your-reservations" class="btn btn-outline-brand1 w-100 link-to-outline-btn" />
          </div>
      </div>



      <button-panic class="btn-to-danger-link d-flex justify-content-end pr-3 mt-5" />

    </loading-container>

    <div class="col-12 d-flex justify-content-end mt-3 px-0">
      <link-faq />
    </div>


  </div>
</template>

<script lang="ts">
import CalendarMonthly from '@/components/calendar/CalendarMonthly.vue'
import CalendarMonthSelector from '@/components/calendar/CalendarMonthSelector.vue'
import ButtonCancel from '@/components/interaction/ButtonCancel.vue'
import ButtonPanic from '@/components/interaction/ButtonPanic.vue'
import LinkArrow from '@/components/interaction/LinkArrow.vue'
import LinkDefault from '@/components/interaction/LinkDefault.vue'
import LinkShoppingCart from '@/components/interaction/LinkShoppingCart.vue'
import LoadingContainer from '@/components/structure/LoadingContainer.vue'
import config from '@/config/config'
import { BookableTimeSlot, Room } from '@/generated/openapi'
import LoadingMixin from '@/mixins/LoadingMixin'
import ShoppingCartTimerMixin from '@/mixins/ShoppingCartTimerMixin'
import { CalendarDay } from '@/models/Calendar'
import { userService } from '@/services/userService'
import { asDays, filterTimeslotsNotOpen } from '@/utils/calendarUtils'
import { isCurrentMonth, now, withDay } from '@/utils/dateTimeUtils'
import { LoadingState } from '@/utils/loadingUtils'
import { urlParamString } from '@/utils/navigationUtils'
import { DateTime } from 'luxon'
import { defineComponent } from 'vue'
import LinkFaq from '@/components/interaction/LinkFaq.vue'
import ErrorMessage from '@/components/feedback/ErrorMessage.vue'

export default defineComponent({
  name: 'Room',
  mixins: [
    LoadingMixin,
    ShoppingCartTimerMixin
  ],
  data() {
    return {
      roomId: '',
      room: {} as Room,
      maxReservations: config.maxReservations,
      calendarForDay: now(),
      timeSlots: [] as BookableTimeSlot[],
      days: [] as CalendarDay[],
      showMaxReservations: false,
      showDuplicateReservation: false
    }
  },
  components: {
    LoadingContainer,
    LinkArrow,
    LinkShoppingCart,
    CalendarMonthly,
    CalendarMonthSelector,
    ButtonCancel,
    ButtonPanic,
    LinkDefault,
    LinkFaq,
    ErrorMessage
  },
  created() {
    this.roomId = urlParamString(this.$route, 'id')
    userService.refreshShoppingCart().then(() => this.loadFromServer())
  },
  methods: {
    loadFromServer() {
      this.state = LoadingState.loading
      Promise.all([
        userService.getRoom(this.roomId),
        userService.findTimeSlots(this.roomId, this.calcFromDate().toISODate(), withDay(this.calendarForDay, this.calendarForDay.daysInMonth).toISODate())
      ]).then(([room, timeslots]) => {
        this.room = room
        this.timeSlots = timeslots || []
        this.days = filterTimeslotsNotOpen(asDays(this.timeSlots, this.calendarForDay))
        this.state = LoadingState.success
      }).catch(this.handleErrorWithI18nString)
    },
    changeMonth(newDate: DateTime) {
      this.calendarForDay = newDate
      this.loadFromServer()
    },
    onTimeSlotClicked([_roomId, timeSlotId]: [string, string]) {
      if (this.$store.state.transactionTickets.find(ticket => ticket.timeSlotId === timeSlotId)) {
        this.showDuplicateReservation = true
      } else if (this.$store.state.transactionTickets.length < config.maxReservations) {
        this.addTimeSlot(timeSlotId)
      } else {
        this.showMaxReservations = true
      }
    },
    addTimeSlot(timeSlotId: string) {
      this.state = LoadingState.loading
      userService.addToShoppingCart(timeSlotId).then(() => {
        return this.loadFromServer()
      }).then(() => {
        this.state = LoadingState.success
      }).catch(this.handleErrorWithI18nString)
    },
    remainingReservationsCount() {
      return this.maxReservations - this.$store.state.transactionTickets.length
    },
    calcFromDate() : DateTime {
      if (isCurrentMonth(this.calendarForDay)) {
        return now()
      }
      return withDay(this.calendarForDay, 1)
    },
  }
});
</script>
